import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { ScrollArea } from '@components/ui/scroll-area';
import { useEntityStore } from '@ContractBuilder/store';
import { ValidationIcon } from '@features/contract-validation/components/ValidationIcon';
import { XIcon } from '@heroicons/react/outline';
import { formatMissingDataId } from '@root/helpers/schema';

interface TemplateValidationChecksProps {
  onClose: () => void;
}

export const TemplateValidationChecks = ({ onClose }: TemplateValidationChecksProps) => {
  const submission = useEntityStore(({ submission }) => submission);

  const totalRequired = submission?.contract_number_of_required_data_points ?? 0;
  const missingItems = submission?.contract_missing_data ?? [];
  const presentCount = totalRequired - missingItems.length;
  const allPresent = missingItems.length === 0;

  return (
    <div id="template-validation-view" className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-7 py-4">
        <h2 className="text-base font-normal text-black">Validation</h2>
        <XIcon className="size-4 cursor-pointer text-gray-500 hover:text-gray-700" onClick={onClose} />
      </div>

      <ScrollArea className="w-full flex-1 overflow-y-auto py-4 pr-3 pl-7">
        <div className="flex flex-col gap-y-3 pr-4">
          {allPresent ? (
            <Alert variant="valid">
              <ValidationIcon type="valid" />
              <AlertTitle>All mandatory datapoints are present in the template.</AlertTitle>
            </Alert>
          ) : (
            <>
              <Alert variant="warning">
                <ValidationIcon type="warning" />
                <AlertTitle>
                  {presentCount} of {totalRequired} mandatory datapoints are present in the template.
                </AlertTitle>
              </Alert>

              {missingItems.map(({ id, title }) => (
                <Alert key={id} variant="warning">
                  <ValidationIcon type="warning" />
                  <AlertTitle>
                    Mandatory datapoint <b>{title}</b> is missing from the template
                  </AlertTitle>
                  <AlertDescription className="capitalize">{formatMissingDataId(id)}</AlertDescription>
                </Alert>
              ))}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
